// CSS colour parsing for SVG paint values. Output is always "#rrggbb" plus a separate alpha.

export interface Rgba {
  readonly hex: string;
  readonly alpha: number;
}

// CSS named colours, packed as name:rrggbb pairs.
const NAMED_SOURCE =
  "aliceblue:f0f8ff antiquewhite:faebd7 aqua:00ffff aquamarine:7fffd4 azure:f0ffff beige:f5f5dc bisque:ffe4c4 " +
  "black:000000 blanchedalmond:ffebcd blue:0000ff blueviolet:8a2be2 brown:a52a2a burlywood:deb887 cadetblue:5f9ea0 " +
  "chartreuse:7fff00 chocolate:d2691e coral:ff7f50 cornflowerblue:6495ed cornsilk:fff8dc crimson:dc143c cyan:00ffff " +
  "darkblue:00008b darkcyan:008b8b darkgoldenrod:b8860b darkgray:a9a9a9 darkgreen:006400 darkgrey:a9a9a9 " +
  "darkkhaki:bdb76b darkmagenta:8b008b darkolivegreen:556b2f darkorange:ff8c00 darkorchid:9932cc darkred:8b0000 " +
  "darksalmon:e9967a darkseagreen:8fbc8f darkslateblue:483d8b darkslategray:2f4f4f darkslategrey:2f4f4f " +
  "darkturquoise:00ced1 darkviolet:9400d3 deeppink:ff1493 deepskyblue:00bfff dimgray:696969 dimgrey:696969 " +
  "dodgerblue:1e90ff firebrick:b22222 floralwhite:fffaf0 forestgreen:228b22 fuchsia:ff00ff gainsboro:dcdcdc " +
  "ghostwhite:f8f8ff gold:ffd700 goldenrod:daa520 gray:808080 green:008000 greenyellow:adff2f grey:808080 " +
  "honeydew:f0fff0 hotpink:ff69b4 indianred:cd5c5c indigo:4b0082 ivory:fffff0 khaki:f0e68c lavender:e6e6fa " +
  "lavenderblush:fff0f5 lawngreen:7cfc00 lemonchiffon:fffacd lightblue:add8e6 lightcoral:f08080 lightcyan:e0ffff " +
  "lightgoldenrodyellow:fafad2 lightgray:d3d3d3 lightgreen:90ee90 lightgrey:d3d3d3 lightpink:ffb6c1 " +
  "lightsalmon:ffa07a lightseagreen:20b2aa lightskyblue:87cefa lightslategray:778899 lightslategrey:778899 " +
  "lightsteelblue:b0c4de lightyellow:ffffe0 lime:00ff00 limegreen:32cd32 linen:faf0e6 magenta:ff00ff maroon:800000 " +
  "mediumaquamarine:66cdaa mediumblue:0000cd mediumorchid:ba55d3 mediumpurple:9370db mediumseagreen:3cb371 " +
  "mediumslateblue:7b68ee mediumspringgreen:00fa9a mediumturquoise:48d1cc mediumvioletred:c71585 " +
  "midnightblue:191970 mintcream:f5fffa mistyrose:ffe4e1 moccasin:ffe4b5 navajowhite:ffdead navy:000080 " +
  "oldlace:fdf5e6 olive:808000 olivedrab:6b8e23 orange:ffa500 orangered:ff4500 orchid:da70d6 palegoldenrod:eee8aa " +
  "palegreen:98fb98 paleturquoise:afeeee palevioletred:db7093 papayawhip:ffefd5 peachpuff:ffdab9 peru:cd853f " +
  "pink:ffc0cb plum:dda0dd powderblue:b0e0e6 purple:800080 rebeccapurple:663399 red:ff0000 rosybrown:bc8f8f " +
  "royalblue:4169e1 saddlebrown:8b4513 salmon:fa8072 sandybrown:f4a460 seagreen:2e8b57 seashell:fff5ee " +
  "sienna:a0522d silver:c0c0c0 skyblue:87ceeb slateblue:6a5acd slategray:708090 slategrey:708090 snow:fffafa " +
  "springgreen:00ff7f steelblue:4682b4 tan:d2b48c teal:008080 thistle:d8bfd8 tomato:ff6347 turquoise:40e0d0 " +
  "violet:ee82ee wheat:f5deb3 white:ffffff whitesmoke:f5f5f5 yellow:ffff00 yellowgreen:9acd32";

const NAMED: ReadonlyMap<string, string> = new Map(
  NAMED_SOURCE.split(" ").map((pair) => {
    const [name, hex] = pair.split(":");
    return [name, `#${hex}`] as [string, string];
  })
);

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));
const toHexByte = (n: number): string => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, "0");

const parseChannel = (token: string): number =>
  token.endsWith("%") ? (parseFloat(token) / 100) * 255 : parseFloat(token);

const parseAlpha = (token: string | undefined): number => {
  if (token === undefined) return 1;
  const value = token.endsWith("%") ? parseFloat(token) / 100 : parseFloat(token);
  return Number.isFinite(value) ? clamp(value, 0, 1) : 1;
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  const hue = (((h % 360) + 360) % 360) / 360;
  const f = (n: number): number => {
    const k = (n + hue * 12) % 12;
    return l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0) * 255, f(8) * 255, f(4) * 255];
};

const parseHex = (body: string): Rgba | null => {
  if (!/^[0-9a-f]+$/i.test(body)) return null;
  const expanded = body.length <= 4 ? body.replace(/./g, (ch) => ch + ch) : body;
  if (expanded.length !== 6 && expanded.length !== 8) return null;
  const alpha = expanded.length === 8 ? parseInt(expanded.slice(6), 16) / 255 : 1;
  return { hex: `#${expanded.slice(0, 6).toLowerCase()}`, alpha };
};

const parseFunction = (fn: string, body: string): Rgba | null => {
  const parts = body
    .replace(/\//g, " ")
    .split(/[\s,]+/)
    .filter(Boolean);
  if (parts.length < 3) return null;
  let rgb: [number, number, number];
  if (fn.startsWith("rgb")) {
    rgb = [parseChannel(parts[0]), parseChannel(parts[1]), parseChannel(parts[2])];
  } else {
    rgb = hslToRgb(parseFloat(parts[0]), parseFloat(parts[1]) / 100, parseFloat(parts[2]) / 100);
  }
  if (!rgb.every(Number.isFinite)) return null;
  return { hex: `#${rgb.map(toHexByte).join("")}`, alpha: parseAlpha(parts[3]) };
};

/** Parses a CSS colour. Returns null for "none", paint servers and anything unrecognised. */
export const parseColor = (raw: string | undefined): Rgba | null => {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (value === "none" || value === "transparent") return null;
  if (value.startsWith("#")) return parseHex(value.slice(1));
  const fn = /^(rgba?|hsla?)\(([^)]*)\)$/.exec(value);
  if (fn) return parseFunction(fn[1], fn[2]);
  const named = NAMED.get(value);
  return named ? { hex: named, alpha: 1 } : null;
};
